import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminLogsDb, settingsDb } from '../../../lib/db/index.js';
import { useAuth } from '../../../lib/firebase/auth-context.js';
import { Spinner } from '../../../components/shared/Spinner.js';
import { Icon } from '../../../components/shared/Icon.js';
import { ConfirmDialog } from '../../../components/shared/ConfirmDialog.js';
import { toAppError } from '../../../lib/utils/error.js';
import { logger } from '../../../lib/utils/log.js';
import { DOMAIN_PATTERN, MAX_DOMAINS } from '../../../lib/db/settings.js';

export function SettingsTab() {
  const { profile } = useAuth();
  const qc = useQueryClient();

  const settingsQ = useQuery({
    queryKey: ['admin', 'settings', 'auth'],
    queryFn: () => settingsDb.getAuthSettingsOrEmpty(),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const [domains, setDomains] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pendingSave, setPendingSave] = useState(false);
  const [touched, setTouched] = useState(false);

  // サーバ取得値で domains を初期化（ユーザーが編集を始めたら上書きしない）
  useEffect(() => {
    if (settingsQ.data && !touched) {
      setDomains(settingsQ.data.allowedEmailDomains);
    }
  }, [settingsQ.data, touched]);

  const saveMut = useMutation({
    mutationFn: async (next: string[]) => {
      const wasInitialized = settingsQ.data?.initialized ?? false;
      const previous = settingsQ.data?.allowedEmailDomains ?? [];
      const saved = await settingsDb.updateAllowedDomains(next);
      if (profile) {
        await adminLogsDb.record(
          { uid: profile.id, handle: profile.handle },
          {
            action: 'update_auth_settings',
            targetType: 'settings',
            targetId: 'auth',
            // 未初期化時は previous=null として「初回作成」であることを記録
            targetSnapshot: {
              previous: wasInitialized ? previous : null,
              next: saved,
            },
          },
        );
      }
      return saved;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'settings', 'auth'] });
      qc.invalidateQueries({ queryKey: ['admin', 'logs'] });
      setError(null);
      setTouched(false);
    },
    onError: (err) => {
      logger.error('failed to update auth settings', {
        err: String(err),
        code: toAppError(err).code,
      });
      setError(toAppError(err).userMessage);
    },
    onSettled: () => setPendingSave(false),
  });

  if (settingsQ.isPending) return <Spinner />;

  const trimmed = draft.trim().toLowerCase();
  const isDuplicate = domains.includes(trimmed);
  const isValidFormat = trimmed.length === 0 || DOMAIN_PATTERN.test(trimmed);
  const canAdd =
    trimmed.length > 0 && isValidFormat && !isDuplicate && domains.length < MAX_DOMAINS;
  const dirty =
    JSON.stringify([...domains].sort()) !==
    JSON.stringify([...(settingsQ.data?.allowedEmailDomains ?? [])].sort());

  const addDomain = () => {
    if (trimmed.length === 0) return;
    if (!isValidFormat) {
      setError('ドメイン形式が不正です（例: kobegakuin.ac.jp）。');
      return;
    }
    if (isDuplicate) {
      setError('このドメインは既に登録されています。');
      return;
    }
    if (domains.length >= MAX_DOMAINS) {
      setError(`許可ドメインは ${MAX_DOMAINS} 件までです。`);
      return;
    }
    setDomains((prev) => [...prev, trimmed]);
    setDraft('');
    setError(null);
    setTouched(true);
  };

  const removeDomain = (d: string) => {
    setDomains((prev) => prev.filter((x) => x !== d));
    setError(null);
    setTouched(true);
  };

  const reset = () => {
    setDomains(settingsQ.data?.allowedEmailDomains ?? []);
    setError(null);
    setTouched(false);
  };

  return (
    <div className="admin-section">
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
          サインアップ許可ドメイン
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
          ここに登録されたメールドメインのみアカウント登録を許可します。完全一致判定（サブドメインは別ドメイン扱い）。
          {!settingsQ.data?.initialized && (
            <span style={{ color: 'var(--accent)', marginLeft: 8 }}>
              ※ 未初期化です。1 件以上登録して保存してください。
            </span>
          )}
        </div>
      </div>

      <div className="admin-row admin-row-form" style={{ marginBottom: 12 }}>
        <input
          type="text"
          className="admin-input"
          placeholder="kobegakuin.ac.jp"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addDomain();
            }
          }}
          maxLength={253}
          aria-invalid={trimmed.length > 0 && !isValidFormat}
        />
        <button
          type="button"
          className="btn xs btn-primary"
          disabled={!canAdd}
          onClick={addDomain}
        >
          <Icon name="plus" size={12} />
          追加
        </button>
      </div>

      {domains.length === 0 ? (
        <div
          style={{
            padding: 24,
            textAlign: 'center',
            color: 'var(--ink-3)',
            border: '1px dashed var(--line)',
            borderRadius: 12,
          }}
        >
          許可ドメインが未設定です。1 件以上登録してください。
        </div>
      ) : (
        <div className="admin-rows">
          {domains.map((d) => (
            <div className="admin-row" key={d}>
              <span className="mono" style={{ flex: 1 }}>@{d}</span>
              <button
                type="button"
                className="btn xs btn-destructive"
                onClick={() => removeDomain(d)}
                disabled={saveMut.isPending}
              >
                削除
              </button>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="auth-error" style={{ marginTop: 12 }}>
          {error}
        </div>
      )}

      <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
        <button
          type="button"
          className="btn btn-primary"
          disabled={
            !dirty ||
            domains.length === 0 ||
            saveMut.isPending ||
            pendingSave
          }
          onClick={() => setPendingSave(true)}
        >
          {saveMut.isPending ? '保存中…' : '保存'}
        </button>
        <button
          type="button"
          className="btn"
          disabled={!dirty || saveMut.isPending}
          onClick={reset}
        >
          リセット
        </button>
      </div>

      <ConfirmDialog
        open={pendingSave}
        title="許可ドメイン設定を更新しますか？"
        description="保存後は新しいドメインでのみサインアップ可能になります。既存ユーザーには影響しません。"
        confirmLabel="保存する"
        onConfirm={() => {
          // 多重送信防止: mutation 中なら無視
          if (saveMut.isPending) return;
          saveMut.mutate(domains);
        }}
        onCancel={() => setPendingSave(false)}
      />
    </div>
  );
}
